/**
 * Offline cashier PIN verifiers — plan/28 (hashes/tokens, not plaintext).
 * PBKDF2 via Web Crypto so Vitest/browser can verify without argon2-wasm.
 */

export function normalizeUserCode(raw: string): string | null {
  const code = raw.trim().toLowerCase();
  if (!code || code.length > 32) return null;
  if (!/^[a-z0-9]+$/.test(code)) return null;
  return code;
}

function toHex(buf: ArrayBuffer): string {
  return [...new Uint8Array(buf)]
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

function timingSafeEqualHex(a: string, b: string): boolean {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i += 1) {
    diff |= a.charCodeAt(i) ^ b.charCodeAt(i);
  }
  return diff === 0;
}

export async function makePinVerifier(input: {
  companyRef: string;
  terminalId: string;
  userCode: string;
  pin: string;
  saltHex: string;
}): Promise<string> {
  const enc = new TextEncoder();
  const keyMaterial = await crypto.subtle.importKey(
    "raw",
    enc.encode(input.pin),
    "PBKDF2",
    false,
    ["deriveBits"],
  );
  const salt = enc.encode(
    `${input.companyRef}|${input.terminalId}|${input.userCode}|${input.saltHex}`,
  );
  const bits = await crypto.subtle.deriveBits(
    {
      name: "PBKDF2",
      hash: "SHA-256",
      salt,
      iterations: 100_000,
    },
    keyMaterial,
    256,
  );
  return toHex(bits);
}

export function newSaltHex(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return toHex(bytes.buffer);
}

export async function verifyPinAgainstCached(input: {
  companyRef: string;
  terminalId: string;
  userCode: string;
  pin: string;
  saltHex: string;
  verifierHex: string;
}): Promise<boolean> {
  const next = await makePinVerifier(input);
  return timingSafeEqualHex(next, input.verifierHex);
}
